import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

/**
 * Seletor compacto do protótipo Zênite (`.sel` → `.sel-btn` "Rótulo: <b>valor</b>"
 * + `.sel-menu`).
 *
 * É um listbox ARIA de verdade: setas, Home/End, Enter/Espaço, Escape, clique
 * fora e retorno de foco ao gatilho (AGENTS §14.3). Só a aparência é a do
 * protótipo; o comportamento é o do Select COSMOS.
 */
export interface CompactOption<T extends string = string> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface CompactSelectProps<T extends string = string> {
  /** Rótulo antes dos dois-pontos ("AI Score", "Funil", "Por página"). */
  prefix: string;
  options: Array<CompactOption<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Nome acessível; por padrão, o prefixo. */
  label?: string;
  align?: "start" | "end";
  disabled?: boolean;
  /** Motivo visível da indisponibilidade (§18). */
  disabledReason?: string;
  /** Ícone fixo antes do rótulo, como a bandeira da prioridade no protótipo. */
  icon?: ReactNode;
  /** Destaca o gatilho quando o filtro está valendo (`.is-on` do protótipo). */
  active?: boolean;
  className?: string;
  id?: string;
}

export function CompactSelect<T extends string = string>({
  prefix,
  options,
  value,
  onChange,
  label,
  align = "start",
  disabled = false,
  disabledReason,
  icon,
  active: on = false,
  className,
  id,
}: CompactSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listId = useId();
  const selected = options.find((option) => option.value === value);
  const name = label ?? prefix;

  useEffect(() => {
    if (!open) return;
    setActive(Math.max(0, options.findIndex((option) => option.value === value)));
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open, options, value]);

  useEffect(() => {
    if (!open) return;
    const options = rootRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    options?.[active]?.focus();
  }, [active, open]);

  const choose = (index: number) => {
    const option = options[index];
    if (!option || option.disabled) return;
    onChange(option.value);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (!open) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActive((index) => Math.min(options.length - 1, index + 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        setActive((index) => Math.max(0, index - 1));
        break;
      case "Home":
        event.preventDefault();
        setActive(0);
        break;
      case "End":
        event.preventDefault();
        setActive(options.length - 1);
        break;
      case "Enter":
      case " ":
        event.preventDefault();
        choose(active);
        break;
      case "Tab":
        setOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div
      ref={rootRef}
      id={id}
      className={[className, "sel", open ? "open" : ""].filter(Boolean).join(" ")}
      onKeyDown={onKeyDown}
    >
      <button
        ref={triggerRef}
        type="button"
        className={on ? "sel-btn is-on" : "sel-btn"}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`${name}: ${selected?.label ?? ""}`}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        {icon}
        {prefix}: <b>{selected?.label ?? "—"}</b>
        <span className="chev" aria-hidden="true">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>
      {disabled && disabledReason && <span className="cx-sr-only">{disabledReason}</span>}
      {open && (
        <div
          id={listId}
          className="sel-menu"
          role="listbox"
          aria-label={name}
          data-align={align === "end" ? "end" : undefined}
        >
          {options.map((option, index) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              aria-disabled={option.disabled || undefined}
              tabIndex={index === active ? 0 : -1}
              className={option.value === value ? "sel-active" : undefined}
              data-active={index === active ? "true" : undefined}
              onMouseEnter={() => setActive(index)}
              onClick={() => choose(index)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
